/**
 * Объём позиции сметы по ведомости объёмов проекта (СМ-07): строка ведомости ПСД или элемент модели IFC, из
 * которой взят объём. «Взять объём» пересчитывает позицию — это может только тот, кто видит суммы сметы.
 */
import { type EstimateItemRow, type TakeoffList, takeoffUnitFactor } from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../../lib/api';
import { Button } from '../../ui/button';
import { toast } from '../../ui/feedback';
import { Select } from '../../ui/form';

const linkCls = 'inline-flex min-h-[44px] items-center text-accent-text underline md:min-h-[28px]';
const qty = (v: number) => new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 4 }).format(v);

export const takeoffsKey = (projectId: string) => ['takeoffs', projectId];

export function ItemTakeoff({
  estimateId,
  projectId,
  item,
  editable,
  money,
  onChanged,
}: {
  estimateId: string;
  projectId: string;
  item: EstimateItemRow;
  editable: boolean;
  money: boolean;
  onChanged: () => void;
}) {
  const qc = useQueryClient();
  const [picking, setPicking] = useState(false);
  const [pick, setPick] = useState('');
  const q = useQuery({
    queryKey: takeoffsKey(projectId),
    queryFn: () => api<TakeoffList>(`/projects/${projectId}/takeoffs`),
    retry: false,
  });
  const save = useMutation({
    mutationFn: (b: { takeoffId?: string | null; applyTakeoff?: boolean }) =>
      api(`/estimates/${estimateId}/items/${item.id}`, { method: 'PATCH', body: b }),
    onSuccess: () => {
      setPicking(false);
      setPick('');
      qc.invalidateQueries({ queryKey: takeoffsKey(projectId) });
      onChanged();
    },
    onError: (e) => toast(e.message, 'danger'),
  });
  const rows = q.data?.rows ?? [];
  // Ведомости у проекта нет — блок не показывается, чтобы не мешать
  if (!rows.length && !item.takeoffId) return null;
  const row = rows.find((r) => r.id === item.takeoffId);
  const factor = row ? takeoffUnitFactor(item.unit, row.unit) : null;
  const want = row && factor !== null ? row.quantity * factor : null;
  const differs = want !== null ? Math.abs(want - item.quantity) > 1e-6 * Math.max(1, want) : false;
  // Сначала строки той же единицы измерения
  // В выборе — только строки той же единицы (или кратной: «м3» к «100 м3»)
  const sorted = rows.filter((r) => takeoffUnitFactor(item.unit, r.unit) !== null);
  return (
    <div className="space-y-1 pb-1 text-[12px]">
      <div className="font-medium text-ink">Объём по ведомости</div>
      <div className="text-ink-2">
        {row ? (
          <span className="text-ink">
            {row.code ? `${row.code} · ` : ''}
            {row.name} — <span className="num">{qty(row.quantity)}</span> {row.unit}
            {row.source === 'ifc'
              ? ' (модель IFC)'
              : row.setCode
                ? ` (${row.setCode}${row.sheetNumber ? `, лист ${row.sheetNumber}` : ''})`
                : ''}
            {row && factor === null ? (
              <span className="text-danger"> · единица позиции «{item.unit}» другая</span>
            ) : null}
            {differs ? (
              <span className="text-danger">
                {' '}
                · в позиции {qty(item.quantity)} {item.unit}
              </span>
            ) : null}
          </span>
        ) : (
          'не связана со строкой ведомости'
        )}
      </div>
      {editable ? (
        <div className="flex flex-wrap items-center gap-x-3">
          {row && differs && money ? (
            <button
              type="button"
              className={linkCls}
              disabled={save.isPending}
              onClick={() => save.mutate({ applyTakeoff: true })}
            >
              Взять объём из ведомости
            </button>
          ) : null}
          {!picking ? (
            <button type="button" className={linkCls} onClick={() => setPicking(true)}>
              {row ? 'Другая строка' : 'Связать со строкой ведомости'}
            </button>
          ) : null}
          {row ? (
            <button
              type="button"
              className={linkCls}
              disabled={save.isPending}
              onClick={() => save.mutate({ takeoffId: null })}
            >
              Отвязать
            </button>
          ) : null}
        </div>
      ) : null}
      {editable && picking ? (
        <div className="flex flex-wrap items-end gap-2">
          <Select
            className="min-h-[44px] max-w-full md:min-h-0"
            aria-label="Строка ведомости объёмов"
            value={pick}
            onChange={(e) => setPick(e.target.value)}
          >
            <option value="">
              {sorted.length ? '— строка ведомости —' : `нет строк в единице «${item.unit}»`}
            </option>
            {sorted.map((r) => (
              <option key={r.id} value={r.id}>
                {r.code ? `${r.code} · ` : ''}
                {r.name.slice(0, 80)} — {qty(r.quantity)} {r.unit}
              </option>
            ))}
          </Select>
          <Button
            className="min-h-[44px] md:min-h-0"
            disabled={!pick || save.isPending}
            onClick={() => save.mutate({ takeoffId: pick })}
          >
            Связать
          </Button>
          {money ? (
            <Button
              variant="secondary"
              className="min-h-[44px] md:min-h-0"
              disabled={!pick || save.isPending}
              onClick={() => save.mutate({ takeoffId: pick, applyTakeoff: true })}
            >
              Связать и взять объём
            </Button>
          ) : null}
          <Button variant="ghost" className="min-h-[44px] md:min-h-0" onClick={() => setPicking(false)}>
            Отмена
          </Button>
        </div>
      ) : null}
    </div>
  );
}
